import React from 'react';
import clsx from 'clsx';
import Participant from '../Participant/Participant';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import useMainParticipant from '../../hooks/useMainParticipant/useMainParticipant';
import useParticipantsContext from '../../hooks/useParticipantsContext/useParticipantsContext';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import useSelectedParticipant from '../VideoProvider/useSelectedParticipant/useSelectedParticipant';
import useScreenShareParticipant from '../../hooks/useScreenShareParticipant/useScreenShareParticipant';
import MenuScreen from '../MenuBar/Menu/MenuScreen';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      overflowY: 'auto',
      background: 'rgb(79, 83, 85)',
      gridArea: '1 / 2 / 1 / 3',
      zIndex: 5,
      [theme.breakpoints.down('sm')]: {
        gridArea: '2 / 1 / 3 / 3',
        overflowY: 'initial',
        overflowX: 'auto',
        display: 'flex',
      },
    },
    transparentBackground: {
      background: 'transparent',
    },
    scrollContainer: {
      display: 'flex',
      justifyContent: 'center',
      position: 'fixed',
      bottom: '0',

      left: '0',
      height: '34%',
      [theme.breakpoints.down('sm')]: {
        left: 0,
      },
    },
    innerScrollContainer: {
      width: `calc(${theme.sidebarWidth}px - 3em)`,
      [theme.breakpoints.down('sm')]: {
        width: '40%',
        position: 'fixed',
        bottom: '75px',
        left: '20px',
        padding: `${theme.sidebarMobilePadding}px`,
        display: 'flex',
      },
    },
    screenMenuContainer: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      height: '100px',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      paddingTop: '12px',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    participantContainer: {
      display: 'flex',
      marginLeft: '88px',
      width: '100%',
      height: '100%',
      flexDirection: 'column',
      justifyContent: 'end',
      '& div': {
        margin: '0',
        left: '0',
        top: '0',
        marginBottom: '120px',
      },
      [theme.breakpoints.down('sm')]: {
        marginLeft: '0px',
        '& div': {
          marginBottom: '10px',
        },
      },
    },
  })
);

export default function ParticipantList() {
  const classes = useStyles();
  const { room } = useVideoContext();
  const localParticipant = room!.localParticipant;
  const { speakerViewParticipants } = useParticipantsContext();
  const [selectedParticipant, setSelectedParticipant] = useSelectedParticipant();
  const screenShareParticipant = useScreenShareParticipant();
  const mainParticipant = useMainParticipant();
  const isRemoteParticipantScreenSharing = screenShareParticipant && screenShareParticipant !== localParticipant;

  if (speakerViewParticipants.length === 0) return null; // Don't render this component if there are no remote participants.

  return (
    <aside
      className={clsx(classes.container, {
        [classes.transparentBackground]: !isRemoteParticipantScreenSharing,
      })}
    >
      <div className={classes.screenMenuContainer}>
        <MenuScreen />
      </div>
      <div className={classes.scrollContainer}>
        <div className={classes.innerScrollContainer}>
          <div className={classes.participantContainer}>
            <Participant participant={localParticipant} isLocalParticipant={true} />
            {speakerViewParticipants.map(participant => {
              const isSelected = participant === selectedParticipant;
              const hideParticipant =
                participant === mainParticipant && participant !== screenShareParticipant && !isSelected;
              return (
                <Participant
                  key={participant.sid}
                  participant={participant}
                  isSelected={participant === selectedParticipant}
                  onClick={() => setSelectedParticipant(participant)}
                  hideParticipant={hideParticipant}
                />
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );
}
